import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Nullable, SelectOption } from 'global';
import { Box, Button, Stack, Alert } from '@mui/material';
import { ListingForm } from 'components/shared/TableListing';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';

import { Template } from '../../api';
import { useBPFMutations } from '../../api';
import { stagesMapper } from '../../mappers';
import { StagesFormValues, TemplateFormValues } from '../../types';
import { StagesConstructorForm } from './form/StagesConstructorForm';
import { StagesConstructorListing } from './listing/StagesConstructorListing';

interface Props {
  accountId: string;
  template: Nullable<Template>;
  isDepartmentsLoading: boolean;
  departmentsOptions: SelectOption[];
}

export const StagesConstructor = ({ accountId, template, departmentsOptions, isDepartmentsLoading }: Props) => {
  const isEdit = Boolean(template);

  const { goBack } = useHistory();

  const { isTemplateCreating, isTemplateUpdating, handleCreateTemplate, handleUpdateTemplate } = useBPFMutations();

  const templateSubmitRef = useRef<{ submit: () => Promise<Nullable<TemplateFormValues>> } | null>(null);
  const stagesSubmitRef = useRef<{ submit: () => Promise<Nullable<ListingForm<StagesFormValues>>> } | null>(null);

  const handleSubmitAll = async () => {
    const templateValues = await templateSubmitRef.current?.submit();
    const stagesValues = await stagesSubmitRef.current?.submit();

    if (templateValues && stagesValues) {
      const createOrUpdateDto = stagesMapper.processFormValuesToCreateOrUpdateDto(
        accountId,
        templateValues,
        stagesValues
      );

      if (isEdit && template) {
        await handleUpdateTemplate({ ...createOrUpdateDto, businessProcessFlowId: template.businessProcessFlowId });
      } else {
        await handleCreateTemplate(createOrUpdateDto);
      }
    }
  };

  return (
    <CardContainer>
      {isEdit && (
        <Alert color="info">
          You only have access to changing template name and stage names, if you need to make other changes, create a
          new template and make it active
        </Alert>
      )}

      <StagesConstructorForm
        template={template}
        submitRef={templateSubmitRef}
        departmentsOptions={departmentsOptions}
        isDepartmentsLoading={isDepartmentsLoading}
      />

      <Box sx={{ overflowY: 'auto', height: '100%' }}>
        <StagesConstructorListing
          isEdit={isEdit}
          submitRef={stagesSubmitRef}
          initialValues={stagesMapper.processTemplateToConstructorFormValues(template)}
        />
      </Box>

      <Stack direction="row" gap="1rem">
        <Button
          size="large"
          variant="contained"
          onClick={handleSubmitAll}
          loading={isTemplateCreating || isTemplateUpdating}
        >
          Save
        </Button>

        <Button onClick={goBack} size="large" disabled={isTemplateCreating || isTemplateUpdating}>
          Cancel
        </Button>
      </Stack>
    </CardContainer>
  );
};
